/**
 * PhoneField — a shared, WhatsApp-style country-code phone input (DNKADM-0084).
 *
 * A curated flag + dial-code country picker beside a national-number field, with
 * light per-country display formatting. The component is CONTROLLED on a single
 * `value: string` that is canonical **E.164** (`+<dialCode><digits>`) and emits
 * E.164 through `onChange`. The national-number mask (`./countries`) is DISPLAY
 * ONLY — it never changes what is stored.
 *
 * Behavior (see the spec / `./phoneFormat`):
 *  - **Empty value** → defaults to the browser-locale country (→ US/NANP fallback),
 *    or the optional `defaultCountry` override; the national field is empty.
 *  - **E.164 value** → parsed (longest dial-code prefix) to re-select the country
 *    and seed the formatted national part; `+1` defaults to the US (NANP note).
 *  - **Legacy / unparseable value** (e.g. `"512-555-1234"`, or a GHL-reformatted
 *    member phone) → shown AS-IS under the default country, never blanked or
 *    auto-rewritten; the admin can correct it via the picker (which re-derives a
 *    clean E.164 from the digits present).
 *  - **Validation is light + non-blocking** — phone is optional at every call-site,
 *    so the field never hard-blocks save. Clearing the number emits `''`.
 *
 * No phone library backs this — it is the lean custom build the spec calls for.
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import {
  Box,
  Button,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icon, resolveTokens } from '@/theme';
import { COUNTRIES, countryByIso, type Country } from './countries';
import {
  detectDefaultCountry,
  digitsOnly,
  formatNational,
  parsePhone,
  toE164,
} from './phoneFormat';

export interface PhoneFieldProps {
  /** Canonical E.164 value (`+<dialCode><digits>`) or `''`. */
  value: string;
  /** Receives the canonical E.164 value (`''` when the number is cleared). */
  onChange: (e164: string) => void;
  /** Visible field label for the national-number input. */
  label?: string;
  /** Disable both the picker and the national field while a save is in flight. */
  disabled?: boolean;
  /** Error state for the national field. */
  error?: boolean;
  /** Helper/hint text under the national field. */
  helperText?: ReactNode;
  /** Accessible name for the national-number input (defaults to `label`). */
  'aria-label'?: string;
  /** Optional ISO override for the default country (else browser locale → US). */
  defaultCountry?: string;
}

/** Seed the country + national display from a value, honoring an ISO override. */
function seed(value: string, overrideIso: string | undefined): {
  country: Country;
  national: string;
} {
  const fallback = (overrideIso && countryByIso(overrideIso)) || detectDefaultCountry();
  const trimmed = value.trim();
  if (trimmed === '') return { country: fallback, national: '' };
  return parsePhone(trimmed, fallback);
}

export function PhoneField({
  value,
  onChange,
  label = 'Phone',
  disabled = false,
  error = false,
  helperText,
  'aria-label': ariaLabel,
  defaultCountry: defaultCountryIso,
}: PhoneFieldProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const ids = useId();
  // Seed once from the initial value; the sync effect below reseeds only when the
  // parent pushes a value we did not emit (an external reset).
  const initial = useMemo(
    () => seed(value, defaultCountryIso),
    // Seed is intentionally computed once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [country, setCountry] = useState<Country>(initial.country);
  const [national, setNational] = useState<string>(initial.national);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  // The last E.164 we emitted — so the value-sync effect ignores our own changes.
  const lastEmitted = useRef(value);
  // Focus the search field once the picker has opened (the a11y-preferred
  // alternative to the discouraged `autoFocus` prop — mirrors ExitFrontDeskDialog).
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Keep the field in sync when the parent pushes a value we didn't emit (e.g. an
  // external reset). Our own emits set `lastEmitted`, so they no-op here.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    const next = seed(value, defaultCountryIso);
    setCountry(next.country);
    setNational(next.national);
    lastEmitted.current = value;
  }, [value, defaultCountryIso]);

  function emit(nextCountry: Country, nextNational: string) {
    const e164 = toE164(nextCountry, nextNational);
    lastEmitted.current = e164;
    onChange(e164);
  }

  function handleNationalChange(raw: string) {
    const formatted = formatNational(country.format, digitsOnly(raw));
    setNational(formatted);
    emit(country, formatted);
  }

  function handleCountrySelect(next: Country) {
    // Re-derive the national display from the digits present (this is also how a
    // legacy/unparseable value is corrected: its digits reformat under the picked
    // country and a clean E.164 is emitted).
    const formatted = formatNational(next.format, digitsOnly(national));
    setCountry(next);
    setNational(formatted);
    emit(next, formatted);
    closeMenu();
  }

  function openMenu(e: MouseEvent<HTMLElement>) {
    setQuery('');
    setAnchorEl(e.currentTarget);
  }
  function closeMenu() {
    setAnchorEl(null);
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return COUNTRIES;
    const digits = q.replace(/\D+/g, '');
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (digits !== '' && c.dialCode.includes(digits)),
    );
  }, [query]);

  const menuOpen = Boolean(anchorEl);
  const searchId = `${ids}-search`;

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
      <Button
        variant="outlined"
        color="inherit"
        onClick={openMenu}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-label={`Country: ${country.name} +${country.dialCode}`}
        sx={{
          flexShrink: 0,
          height: 40,
          px: 1.25,
          borderColor: theme.palette.mode === 'dark' ? t.lineStrong : 'rgba(0,0,0,0.23)',
          color: 'text.primary',
          textTransform: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <Box component="span" sx={{ fontSize: 18, lineHeight: 1, mr: 0.5 }} aria-hidden>
          {country.flag}
        </Box>
        <Box component="span" sx={{ fontSize: 14 }}>
          +{country.dialCode}
        </Box>
        <Icon
          name="arrow_drop_down"
          sx={{
            fontSize: 20,
            ml: 0.25,
            color: theme.palette.mode === 'dark' ? t.text2 : 'rgba(0,0,0,0.54)',
          }}
        />
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        disableAutoFocusItem
        slotProps={{
          paper: { sx: { maxHeight: 360, width: 280 } },
          transition: { onEntered: () => searchRef.current?.focus() },
        }}
      >
        <Box sx={{ px: 1.5, pt: 0.5, pb: 1 }}>
          <TextField
            id={searchId}
            inputRef={searchRef}
            fullWidth
            size="small"
            placeholder="Search countries"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // Keep keystrokes from triggering the Menu's built-in typeahead/close.
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            slotProps={{ htmlInput: { 'aria-label': 'Search countries' } }}
          />
        </Box>
        {filtered.map((c) => (
          <MenuItem
            key={c.iso}
            selected={c.iso === country.iso}
            onClick={() => handleCountrySelect(c)}
          >
            <Box component="span" sx={{ fontSize: 18, mr: 1.25 }} aria-hidden>
              {c.flag}
            </Box>
            <ListItemText primary={c.name} />
            <Typography component="span" sx={{ ml: 1.5, color: 'text.secondary', fontSize: 13 }}>
              +{c.dialCode}
            </Typography>
          </MenuItem>
        ))}
        {filtered.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No matching countries" />
          </MenuItem>
        )}
      </Menu>

      <TextField
        label={label}
        value={national}
        onChange={(e) => handleNationalChange(e.target.value)}
        disabled={disabled}
        size="small"
        fullWidth
        error={error}
        {...(helperText !== undefined ? { helperText } : {})}
        type="tel"
        slotProps={{ htmlInput: { 'aria-label': ariaLabel ?? label, inputMode: 'tel' } }}
      />
    </Stack>
  );
}
